import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { AdminService } from '@generated/services/admin.service';
import { AuthService } from '@core/services/auth.service';
import { NotificationService } from '@core/services/notification.service';
import { UserListItem } from '@generated/models/user-list-item';
import { GenerateUserTokenRequest } from '@generated/models/generate-user-token-request';

@Component({
  selector: 'app-admin-sign-in',
  standalone: true,
  imports: [
    FormsModule,
    SelectModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule
],
  templateUrl: './admin-sign-in.html',
  styleUrls: ['./admin-sign-in.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSignInComponent implements OnInit {
  users: UserListItem[] = [];
  selectedUserId: string | null = null;
  isLoading = false;
  isSigningIn = false;
  signedInUserEmail: string | null = null;
  isSignedIn = false;

  constructor(
    private adminService: AdminService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.checkSignedInStatus();
    // Only load users if not signed in
    if (!this.isSignedIn) {
      this.loadUsers();
    }
  }

  private checkSignedInStatus(): void {
    const token = localStorage.getItem('signed-in-as-user-token');
    if (token) {
      try {
        const email = this.getEmailFromToken(token);
        if (email) {
          this.isSignedIn = true;
          this.signedInUserEmail = email;
          this.cdr.markForCheck();
        }
      } catch (error) {
        console.error('Invalid token in localStorage:', error);
        localStorage.removeItem('signed-in-as-user-token');
      }
    }
  }

  private loadUsers(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.adminService.apiAdminUsersGet().subscribe({
      next: (users) => {
        this.users = users || [];

        // If signed in, try to find and select the current user
        if (this.isSignedIn && this.signedInUserEmail) {
          const currentUser = this.users.find(u => u.email === this.signedInUserEmail);
          if (currentUser) {
            this.selectedUserId = currentUser.id || null;
          }
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load users:', error);
        this.notificationService.showError('Failed to load users list');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  signIn(): void {
    if (!this.selectedUserId) {
      this.notificationService.showWarning('Please select a user');
      return;
    }

    this.isSigningIn = true;
    this.cdr.markForCheck();

    const request: GenerateUserTokenRequest = {
      user_id: this.selectedUserId,
      expiration_hours: 24 // 24 hours expiration
    };

    this.adminService.apiAdminGenerateUserTokenPost({ body: request }).subscribe({
      next: (result) => {
        if (result.token) {
          // Save token to localStorage
          localStorage.setItem('signed-in-as-user-token', result.token);

          // Update UI state
          this.isSignedIn = true;
          this.signedInUserEmail = result.email || 'Unknown';

          this.notificationService.showSuccess(`Signed in as ${this.signedInUserEmail}`);

          // Reload the page to apply new authentication
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          this.notificationService.showError('Failed to generate user token');
        }

        this.isSigningIn = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to sign in:', error);
        this.notificationService.showError('Failed to sign in as selected user');
        this.isSigningIn = false;
        this.cdr.markForCheck();
      }
    });
  }

  signOut(): void {
    localStorage.removeItem('signed-in-as-user-token');
    this.isSignedIn = false;
    this.signedInUserEmail = null;
    this.selectedUserId = null;
    this.notificationService.showSuccess('Signed out successfully');
    this.cdr.markForCheck();

    // Load users list after signing out
    this.loadUsers();

    // Reload the page to apply normal authentication
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  private getEmailFromToken(token: string): string | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const decoded = JSON.parse(jsonPayload);
      return decoded.email || decoded.sub || null;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  getUserOptions(): any[] {
    return this.users.map(user => ({
      label: user.email || 'Unknown',
      value: user.id
    }));
  }
}